"use client";

import { useState } from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { AnimatedBuildingBackground } from "@/components/ui/AnimatedBuildingBackground";
import { userApi } from "@/lib/api/user";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      await userApi.forgotPassword({ email });
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || "Failed to send reset link.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4 relative z-0">
        <AnimatedBuildingBackground />
        <div className="absolute top-8 left-8">
          <Link href="/" className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 bg-primary/10 hover:bg-primary/20 px-4 py-2 rounded-full border border-primary/20 transition-all backdrop-blur-sm">
            &larr; Back to Home
          </Link>
        </div>
        <Card 
          className="w-full max-w-lg p-4 sm:p-8 backdrop-blur-xl relative z-10 bg-card/90 border-primary/30 shadow-2xl"
        >
          <CardHeader>
            <CardTitle className="text-2xl text-center">Reset Link Sent</CardTitle>
            <CardDescription className="text-center text-lg mt-2">
              If an account exists with <span className="font-semibold">{email}</span>, 
              we have sent a password reset link.
            </CardDescription>
          </CardHeader>
          <CardFooter className="flex justify-center bg-transparent border-none pt-4">
            <Link href="/login" className="w-full">
              <Button variant="outline" className="w-full">Back to Login</Button>
            </Link>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4 relative z-0">
      <AnimatedBuildingBackground />
      <div className="absolute top-8 left-8">
        <Link href="/" className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 bg-primary/10 hover:bg-primary/20 px-4 py-2 rounded-full border border-primary/20 transition-all backdrop-blur-sm">
          &larr; Back to Home
        </Link>
      </div>
      <Card 
        className="w-full max-w-lg p-4 sm:p-8 backdrop-blur-xl relative z-10 bg-card/90 border-primary/30 shadow-2xl"
      >
        <CardHeader className="space-y-2">
          <CardTitle className="text-2xl font-bold tracking-tight text-center">Reset Password</CardTitle>
          <CardDescription className="text-center">
            Enter your email to receive a password reset link.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/50 text-red-500 rounded-md text-sm">
                {error}
              </div>
            )}
            
            <div className="space-y-3">
              <Label htmlFor="email" className="text-base text-muted-foreground">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="john.doe@example.com"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <Button type="submit" className="w-full text-base font-semibold" disabled={loading}>
              {loading ? "Sending link..." : "Send Reset Link"}
            </Button>
          </form>
        </CardContent>
        <CardFooter className="flex justify-center bg-transparent border-none pb-2 pt-4">
          <p className="text-base text-muted-foreground">
            Remember your password?{" "}
            <Link href="/login" className="text-primary hover:underline">
              Log in
            </Link>
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}
